import { Action, AsyncAction } from '../types/types';
import {
	GetBroadcastServer,
	StartBroadcast,
	StopBroadcast,
	ManuallyCreateBroadcastServer,
	AutoStartBroadcast,
	SetDatabaseStreamState,
	GetDatabaseStreamState,
	ResetBroadcastServer,
	StartSpielbergBroadcast,
	StopSpielbergBroadcast,
	AddDestination,
	UpdateDestination,
	DeleteDestination,
} from '../../connection/broadcasting';
import { EditRecordingMetadata } from '../../connection/media';
import { BroadcastEncodingOptions, BroadcastStats, Destination } from '../types/broadcasting';

export const GET_BROADCAST_SERVER = 'GET_BROADCAST_SERVER';
export function getBroadcastServer(token: string, meeting: number): AsyncAction {
	return {
		type: GET_BROADCAST_SERVER,
		promise: GetBroadcastServer(token, meeting),
	};
}

export const START_BROADCAST = 'START_BROADCAST';
export function startBroadcast(
	token: string,
	meeting: number,
	resolution: string,
	broadcast?: boolean,
	record?: boolean,
): AsyncAction {
	return {
		type: START_BROADCAST,
		promise: StartBroadcast(token, meeting, resolution, broadcast, record),
	};
}

export const STOP_BROADCAST = 'STOP_BROADCAST';
export function stopBroadcast(token: string, meeting: number): AsyncAction {
	return {
		type: STOP_BROADCAST,
		promise: StopBroadcast(token, meeting),
	};
}

export const MANUALLY_CREATE_BROADCAST_SERVER = 'MANUALLY_CREATE_BROADCAST_SERVER';
export function manuallyCreateBroadcastServer(token: string, meeting: number): AsyncAction {
	return {
		type: MANUALLY_CREATE_BROADCAST_SERVER,
		promise: ManuallyCreateBroadcastServer(token, meeting),
	};
}

export const SET_BROADCAST_STATE = 'SET_BROADCAST_STATE';
export function setBroadcastState(state: number): Action {
	return {
		type: SET_BROADCAST_STATE,
		payload: state,
	};
}

export const SET_PREVIOUS_BROADCAST_STATE = 'SET_PREVIOUS_BROADCAST_STATE';
export function setPreviousBroadcastState(state: number): Action {
	return {
		type: SET_PREVIOUS_BROADCAST_STATE,
		payload: state,
	};
}

export const SET_BROADCAST_STATS = 'SET_BROADCAST_STATS';
export function setBroadcastStats(stats: BroadcastStats): Action {
	return {
		type: SET_BROADCAST_STATS,
		payload: stats,
	};
}

export const SET_STREAM_STATE = 'SET_STREAM_STATE';
export function setStreamState(status: number) {
	return {
		type: SET_STREAM_STATE,
		payload: status,
	};
}

export const SET_DB_STREAM_STATE = 'SET_DB_STREAM_STATE';
export function setDatabaseStreamState(
	token: string,
	meeting: number,
	status: number,
): AsyncAction {
	return {
		type: SET_DB_STREAM_STATE,
		promise: SetDatabaseStreamState(token, meeting, status),
	};
}

export const GET_DB_STREAM_STATE = 'GET_DB_STREAM_STATE';
export function getDatabaseStreamState(token: string, meeting: number): AsyncAction {
	return {
		type: GET_DB_STREAM_STATE,
		promise: GetDatabaseStreamState(token, meeting),
	};
}

export const AUTO_START_BROADCAST = 'AUTO_START_BROADCAST';
export function autoStartBroadcast(token: string, meeting: number): AsyncAction {
	return {
		type: AUTO_START_BROADCAST,
		promise: AutoStartBroadcast(token, meeting),
	};
}

export const SET_GO_LIVE_BUTTON_CLICKED = 'SET_GO_LIVE_BUTTON_CLICKED';
export function setGoLiveButtonClicked(goLiveButtonClicked: boolean): Action {
	return {
		type: SET_GO_LIVE_BUTTON_CLICKED,
		payload: goLiveButtonClicked,
	};
}

export const RESET_BROADCAST_SERVER = 'RESET_BROADCAST_SERVER';
export function resetBroadcastServer(token: string, meeting: number): AsyncAction {
	return {
		type: RESET_BROADCAST_SERVER,
		promise: ResetBroadcastServer(token, meeting),
	};
}

export const SET_RECORDING_OPTION = 'SET_RECORDING_OPTION';
export function setRecordingOption(selectedRecordingOption: number): Action {
	return {
		type: SET_RECORDING_OPTION,
		payload: selectedRecordingOption,
	};
}

export const SYNC_BROADCAST_REDUCER_STATE = 'SYNC_BROADCAST_REDUCER_STATE';
export function syncBroadcastReducerState(state: any): Action {
	return {
		type: SYNC_BROADCAST_REDUCER_STATE,
		payload: state,
	};
}

export const RESET_BROADCAST_REDUCER_STATE = 'RESET_BROADCAST_REDUCER_STATE';
export function resetBroadcastReducerState(): Action {
	return {
		type: RESET_BROADCAST_REDUCER_STATE,
		payload: undefined,
	};
}

export const SET_BROADCAST_STARTING_SHOT = 'SET_BROADCAST_STARTING_SHOT';
export function setBroadcastStartingShot(shotId: number): Action {
	return {
		type: SET_BROADCAST_STARTING_SHOT,
		payload: shotId,
	};
}

export const SET_BROADCAST_STARTED_BY = 'SET_BROADCAST_STARTED_BY';
export function setBroadcastStartedBy(userId: number): Action {
	return {
		type: SET_BROADCAST_STARTED_BY,
		payload: userId,
	};
}

export const SET_CURRENT_RECORDING_STREAM_ID = 'SET_CURRENT_RECORDING_STREAM_ID';
export function setCurrentRecordingStreamId(streamId: string): Action {
	return {
		type: SET_CURRENT_RECORDING_STREAM_ID,
		payload: streamId,
	};
}

export const EDIT_RECORDING_METADATA = 'EDIT_RECORDING_METADATA';
export function editRecordingMetadata(
	token: string,
	streamId: string,
	broadcastStartedBy: number,
	broadcastStartingShotId: number,
): AsyncAction {
	return {
		type: EDIT_RECORDING_METADATA,
		promise: EditRecordingMetadata(token, streamId, broadcastStartedBy, broadcastStartingShotId),
	};
}

export const SET_GO_LIVE_FROM_MODAL = 'GO_LIVE_FROM_MODAL';
export function setGoLiveFromModal(shouldGoLive: boolean): Action {
	return {
		type: SET_GO_LIVE_FROM_MODAL,
		payload: shouldGoLive,
	};
}

export const SET_ADDING_DESTINATION = 'SET_ADDING_DESTINATION';
export function setAddingDestination(
	addingDestination: Destination | null,
): Action {
	return {
		type: SET_ADDING_DESTINATION,
		payload: addingDestination,
	};
}

export const SET_EDITING_DESTINATION = 'SET_EDITING_DESTINATION';
export function setEditingDestination(editingDestination: string): Action {
	return {
		type: SET_EDITING_DESTINATION,
		payload: editingDestination,
	};
}

export const START_SPIELBERG_BROADCAST = 'START_SPIELBERG_BROADCAST';
export function startSpielbergBroadcast(
	token: string,
	meetingId: number,
	broadcast?: boolean,
	record?: boolean,
	encodingOptionOverrides?: BroadcastEncodingOptions,
	errorCallback?: () => void,
): AsyncAction {
	return {
		type: START_SPIELBERG_BROADCAST,
		promise: StartSpielbergBroadcast(
			token,
			meetingId,
			broadcast,
			record,
			encodingOptionOverrides,
			errorCallback,
		),
	};
}

export const STOP_SPIELBERG_BROADCAST = 'STOP_SPIELBERG_BROADCAST';
export function stopSpielbergBroadcast(token: string, meetingId: number): AsyncAction {
	return {
		type: STOP_SPIELBERG_BROADCAST,
		promise: StopSpielbergBroadcast(token, meetingId),
	};
}

export const ADD_DESTINATION = 'ADD_DESTINATION';
export function addDestination(
	token: string,
	meetingId: number,
	destination: Destination,
): AsyncAction {
	return {
		type: ADD_DESTINATION,
		promise: AddDestination(token, meetingId, destination),
	};
}

export const UPDATE_DESTINATION = 'UPDATE_DESTINATION';
export function updateDestination(
	token: string,
	meetingId: number,
	destination: Destination,
): AsyncAction {
	return {
		type: UPDATE_DESTINATION,
		promise: UpdateDestination(token, meetingId, destination),
	};
}

export const DELETE_DESTINATION = 'DELETE_DESTINATION';
export function deleteDestination(
	token: string,
	meetingId: number,
	destinationId: number,
): AsyncAction {
	return {
		type: DELETE_DESTINATION,
		promise: DeleteDestination(token, meetingId, destinationId),
	};
}
